/**
 * La règle du mode enfant verrouillé (HARMONIA-87), isolée du routeur pour
 * être testée sans lui : un parent a posé son appareil devant ses enfants, et
 * rien ne doit sortir de /child-mode sans le code parent.
 *
 * Fonction pure : le routeur lui donne l'état, elle répond quoi faire.
 */
export const CHILD_MODE_HOME = '/child-mode/tasks'
/** La journée de la famille, sur la tablette partagée (cf. utils/sharedTablet). */
export const CHILD_MODE_FAMILY = '/child-mode/family'

export type ChildModeLockDecision =
  /** Pas de verrou : la navigation suit son cours. */
  | { action: 'none' }
  /** Le verrou n'a plus rien à garder : on le lève, puis on laisse passer. */
  | { action: 'release'; reason: 'no-session' | 'no-child' }
  /** Déjà dans le mode enfant : on laisse passer, en changeant d'enfant si besoin. */
  | { action: 'stay'; switchTo: string | null }
  /** Tentative de sortie sans code : retour au mode enfant. */
  | { action: 'redirect'; to: string; switchTo: string | null }
  /**
   * Téléphone appairé dont l'enfant n'a plus droit à son espace : on le
   * déconnecte. Lever le verrou, comme sur une tablette, ouvrirait les écrans
   * parent dans les mains de l'enfant.
   */
  | { action: 'unpair' }

export function decideChildModeLock(input: {
  locked: boolean
  hasParentSession: boolean
  /** L'enfant que le mode enfant peut montrer (cf. appContext.childModeTargetId). */
  targetChildId: string | null
  currentChildId: string | null
  toPath: string
  /** Le téléphone est appairé à cet enfant (appContext.pairedDevice). */
  pairedChildId?: string | null
  /**
   * L'enfant appairé a-t-il droit à son espace ? `null` tant qu'on ne le sait
   * pas (foyer pas encore chargé, hors ligne) : on garde alors le verrou, on ne
   * déconnecte que sur un refus établi.
   */
  pairedChildAllowed?: boolean | null
}): ChildModeLockDecision {
  // Un téléphone appairé reste verrouillé même si le drapeau du verrou a
  // disparu : il n'a aucune sortie (#402).
  if (!input.locked && !input.pairedChildId) return { action: 'none' }

  // Déconnexion ou session expirée : sans parent connecté, Rule 3 renvoie vers
  // /welcome et ce verrou renverrait vers /child-mode, une boucle.
  if (!input.hasParentSession) return { action: 'release', reason: 'no-session' }

  // Téléphone appairé : un seul enfant, jamais un autre, et jamais les écrans
  // parent.
  if (input.pairedChildId) {
    if (input.pairedChildAllowed === false) return { action: 'unpair' }
    const switchTo = input.pairedChildId !== input.currentChildId ? input.pairedChildId : null
    // La journée de la famille est celle de la tablette partagée : sur le
    // téléphone d'un enfant, elle montrerait les tâches de ses frères et sœurs.
    if (input.toPath === CHILD_MODE_FAMILY) return { action: 'redirect', to: CHILD_MODE_HOME, switchTo }
    if (input.toPath === '/child-mode' || input.toPath.startsWith('/child-mode/')) {
      return { action: 'stay', switchTo }
    }
    return { action: 'redirect', to: CHILD_MODE_HOME, switchTo }
  }

  // Plus aucun enfant n'y a droit (abonnement `legacy` expiré pendant que la
  // tablette était verrouillée) : Rule 3c renverrait vers l'accueil, ce verrou
  // vers le mode enfant, une boucle aussi. Cas rare et assumé.
  if (!input.targetChildId) return { action: 'release', reason: 'no-child' }

  const switchTo = input.targetChildId !== input.currentChildId ? input.targetChildId : null
  if (input.toPath === '/child-mode' || input.toPath.startsWith('/child-mode/')) {
    return { action: 'stay', switchTo }
  }
  return { action: 'redirect', to: CHILD_MODE_HOME, switchTo }
}
